import { addDays } from "date-fns";
import throttle from "lodash/throttle";
/**
 * References
 *  => original https://www.npmjs.com/package/exit-intent
 *  => some tips https://www.thepolyglotdeveloper.com/2018/11/using-exit-intent-listener-manage-popups-page/
 */
export default function exitIntent(options = {}) {
  const defaultOptions = {
    debug: false,
    maxInvokeCount: 1,
    eventThrottle: 200,
    mouseLeaveThreshold: 20,
    invokeAfterInactiveSecondsDesktop: 3,
    invokeAfterInactiveSecondsMobile: 8,
    invokeAgainAfterSeconds: 10,
    onExitIntent: () => {},
    isMobile: false,
  };
  const config = { ...defaultOptions, ...options };
  const log = (...args) => {
    if (config.debug) {
      // eslint-disable-next-line no-console
      console.log("[exit-intent]", ...args);
    }
  };

  const isDesktop = !config.isMobile;
  const listeners = [];
  let invokeCount = 0;
  let timer;

  // Invoke callback
  const _invokeCallback = () => {
    if (invokeCount < config.maxInvokeCount) {
      invokeCount++;
      log("invoke onExitIntent", invokeCount);
      config.onExitIntent();
      if (invokeCount >= config.maxInvokeCount) {
        clearAll();
      }
    }
  };

  // limit invocation to config.invokeAgainAfterSeconds to make sure that we do NOT bug the user too often
  const invokeCallback = throttle(
    () => {
      try {
        _invokeCallback();
      } catch (err) {
        if (window.Sentry) {
          window.Sentry.captureException(err, {
            tags: { source: "exit_intent" },
          });
        }
      }
    },
    config.invokeAgainAfterSeconds * 1000,
    {
      trailing: false,
    },
  );

  const timeoutOnDevice = isDesktop
    ? config.invokeAfterInactiveSecondsDesktop
    : config.invokeAfterInactiveSecondsMobile;

  const restartTimer = () => {
    if (timer) window.clearTimeout(timer);

    timer = window.setTimeout(() => {
      log("do something after timeout");
      if (isDesktop) {
        registerMouseLeave();
      } else {
        invokeCallback();
      }
    }, timeoutOnDevice * 1000);
  };

  const registerEvent = (event, target) => {
    log("registering event for restartTimer", event, target);
    const listener = throttle(event => {
      log("throttled listener", event);
      restartTimer();
    }, config.eventThrottle);

    target.addEventListener(event, listener, false);
    listeners.push({ event, listener, target });
    return listener;
  };

  const registerMouseLeave = () => {
    log("register mouseleave");
    const onMouseLeave = e => {
      if (!(e instanceof MouseEvent)) return;

      log("mouseleave", e.clientY);
      // Trigger only if mouse leave through top border
      if (e.clientY <= config.mouseLeaveThreshold) invokeCallback();
    };

    const onMouseLeaveListener = throttle(onMouseLeave, config.eventThrottle);
    const event = "mouseleave";
    document.body.addEventListener(event, onMouseLeaveListener, false);
    listeners.push({
      event,
      listener: onMouseLeaveListener,
      target: document.body,
    });
  };

  // Clean up all registered listeners and timer
  const clearAll = () => {
    log("removeEvents");

    listeners.forEach(theListener => {
      const { event, listener, target } = theListener;
      target.removeEventListener(event, listener);
    });

    if (timer) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  // Execute!
  if (!isDesktop) {
    registerEvent("touchstart", document.body);
    registerEvent("touchend", document.body);
    registerEvent("touchmove", document.body);
    registerEvent("keydown", document.body);
  }
  restartTimer(); // start initial timer

  return clearAll;
}

export function isExitIntentViewed(getCookie = () => {}) {
  return getCookie(EXIT_INTENT_VIEWED) || false;
}

export function setExitIntentViewed(
  setCookie = () => {},
  destroyCookie = () => {},
) {
  // Need to set expires, or it will be gone on the next session
  setCookie(EXIT_INTENT_VIEWED, true, { expires: addDays(new Date(), 365) });
  destroyCookie(EXIT_INTENT_DATA);
}

export function setExitIntentData(setCookie = () => {}, data) {
  setCookie(EXIT_INTENT_DATA, data, { expires: addDays(new Date(), 7) });
}

export function getExitIntentData(getCookie = () => {}) {
  return getCookie(EXIT_INTENT_DATA) || null;
}

const EXIT_INTENT_DATA = "exitIntentData";
const EXIT_INTENT_VIEWED = "exitIntentViewed";
